import { Fragment, type ReactNode } from 'react';

import { Box, Divider, Group, Paper, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { IconTrash } from '@tabler/icons-react';

import { DateDisplayView } from '../../atoms/DateDisplay';
import { HoursDisplayView } from '../../atoms/HoursDisplay';
import { IconButtonView } from '../../atoms/IconButton';
import { LabeledValueView } from '../../atoms/LabeledValue';
import { MoneyDisplayView } from '../../atoms/MoneyDisplay';
import type { LabeledValueListItem, LabeledValueListViewProps } from './LabeledValueList.types';

const PROPERTY_LABEL_WIDTH = 130;

function resolveValue(item: LabeledValueListItem): ReactNode {
  if (item.value !== undefined) {
    return typeof item.value === 'string' || typeof item.value === 'number'
      ? String(item.value)
      : item.value;
  }
  if (item.money !== undefined) return <MoneyDisplayView amount={item.money} fw={500} />;
  if (item.hours !== undefined) return <HoursDisplayView hours={item.hours} fw={500} />;
  if (item.date !== undefined) return <DateDisplayView date={item.date} format={item.dateFormat} />;
  return null;
}

function BorderedChip({ value, underline = false }: { value: ReactNode; underline?: boolean }) {
  if (typeof value === 'string') {
    return (
      <Paper withBorder px="xs" radius="sm" style={{ minWidth: 0 }}>
        <Text size="sm" fw={500} td={underline ? 'underline dotted' : undefined}>
          {value}
        </Text>
      </Paper>
    );
  }
  return (
    <Box style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 4 }}>
      {value}
    </Box>
  );
}

function EditableValue({
  value,
  editor,
}: {
  value: ReactNode;
  editor: (close: () => void) => ReactNode;
}) {
  const [opened, { close, toggle }] = useDisclosure(false);

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom-end"
      withArrow
      withinPortal={false}
      closeOnClickOutside
      shadow="md"
    >
      <Popover.Target>
        <UnstyledButton onClick={toggle}>
          <BorderedChip value={value} underline />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p="xs">{editor(close)}</Popover.Dropdown>
    </Popover>
  );
}

function ClickableValue({ value, onClick }: { value: ReactNode; onClick: () => void }) {
  return (
    <UnstyledButton onClick={onClick}>
      <BorderedChip value={value} />
    </UnstyledButton>
  );
}

function resolveValueNode(item: LabeledValueListItem, value: ReactNode): ReactNode {
  if (item.onClick) return <ClickableValue value={value} onClick={item.onClick} />;
  return value;
}

function ItemRow(item: LabeledValueListItem) {
  const value = resolveValueNode(item, resolveValue(item));

  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.editor ? (
          <Group justify="space-between" gap="sm" wrap="nowrap" align="center">
            <Text size="xs" c="dimmed">
              {item.label}
            </Text>
            <EditableValue value={value} editor={item.editor} />
          </Group>
        ) : (
          <LabeledValueView align="horizontal" label={item.label} value={value} />
        )}
        {item.caption}
      </div>
      {item.onRemove && (
        <IconButtonView
          icon={IconTrash}
          label="Fjern"
          onClick={item.onRemove}
          color="red"
          size="sm"
        />
      )}
    </Group>
  );
}

function PropertyRow(item: LabeledValueListItem) {
  const value = resolveValueNode(item, resolveValue(item));

  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <Text size="xs" c="dimmed" w={PROPERTY_LABEL_WIDTH} flex="0 0 auto">
        {item.label}
      </Text>
      <Box style={{ flex: 1, minWidth: 0 }}>
        {item.editor ? (
          <EditableValue value={value} editor={item.editor} />
        ) : typeof value === 'string' ? (
          <Text size="sm" fw={500}>
            {value}
          </Text>
        ) : (
          value
        )}
      </Box>
      {item.onRemove && (
        <IconButtonView
          icon={IconTrash}
          label="Fjern"
          onClick={item.onRemove}
          color="red"
          size="sm"
        />
      )}
    </Group>
  );
}

function TotalRow({ total, variant }: { total: LabeledValueListItem; variant: 'item' | 'property' }) {
  return (
    <>
      <Divider />
      <Group gap="sm" wrap="nowrap" justify={variant === 'property' ? undefined : 'space-between'}>
        <Text size="sm" fw={700} w={variant === 'property' ? PROPERTY_LABEL_WIDTH : undefined} flex="0 0 auto">
          {total.label}
        </Text>
        <Text size="sm" fw={700}>
          {resolveValue(total)}
        </Text>
      </Group>
    </>
  );
}

export function LabeledValueListView({
  items,
  dividers = false,
  total,
  variant = 'item',
}: LabeledValueListViewProps) {
  const Row = variant === 'property' ? PropertyRow : ItemRow;

  return (
    <Stack gap="sm">
      {items.map((item, index) => (
        <Fragment key={item.label}>
          {dividers && index > 0 && <Divider />}
          <Row {...item} />
        </Fragment>
      ))}

      {total && <TotalRow total={total} variant={variant} />}
    </Stack>
  );
}
